import React, { useState, useEffect } from 'react';

const CheckboxGroup = ({
	options = [],
	value = null,
	onValueChange,
	label,
	colors = {
		checked: {
			background: '#F4F3FF',
			text: '#6938EF',
			border: '#F4F3FF',
			checkbox: {
				background: '#6938EF',
				border: '#6938EF'
			}
		},
		unchecked: {
			background: '#FFFFFF',
			text: '#6938EF',
			border: '#EBE9FE',
			checkbox: {
				background: '#6938EF',
				border: '#6938EF'
			}
		}
	},
	disabled = false,
	className = '',
	style = {},
	allowMultiple = false
}) => {
	const [selectedValues, setSelectedValues] = useState(
		allowMultiple ? (Array.isArray(value) ? value : []) : value
	);
	const [isDisabled, setIsDisabled] = useState(disabled);

	useEffect(() => {
		setSelectedValues(
			allowMultiple ? (Array.isArray(value) ? value : []) : value
		);
	}, [value, allowMultiple]);

	useEffect(() => {
		setIsDisabled(disabled);
	}, [disabled]);

	const handleOptionClick = async optionValue => {
		if (isDisabled) return;

		setIsDisabled(true);

		let newValue;

		if (allowMultiple) {
			const currentValues = Array.isArray(selectedValues) ? selectedValues : [];
			const isSelected = currentValues.some(
				v => v?.value === optionValue?.value
			);

			if (isSelected) {
				newValue = currentValues.filter(v => v?.value !== optionValue?.value);
			} else {
				newValue = [...currentValues, optionValue];
			}
		} else {
			newValue =
				selectedValues?.value === optionValue?.value ? null : optionValue;
		}

		setSelectedValues(newValue);

		if (onValueChange) {
			try {
				const valueUpdated = await onValueChange(newValue);
				if (valueUpdated === false) {
					setSelectedValues(
						allowMultiple ? (Array.isArray(value) ? value : []) : value
					);
				}
			} catch (error) {
				setSelectedValues(
					allowMultiple ? (Array.isArray(value) ? value : []) : value
				);
			}
		}

		setIsDisabled(false);
	};

	const isOptionSelected = optionValue => {
		if (allowMultiple) {
			return (
				Array.isArray(selectedValues) &&
				selectedValues.some(v => v?.value === optionValue?.value)
			);
		}
		return selectedValues?.value === optionValue?.value;
	};

	const getOptionColors = (option, isSelected) => {
		if (option.colors) {
			return isSelected
				? {
						...option.colors,
						background: option.colors.border
					}
				: {
						...option.colors,
						background: 'transparent'
					};
		}

		return isSelected ? colors.checked : colors.unchecked;
	};

	return (
		<div className={`checkbox-group-container ${className}`} style={style}>
			<div className="checkbox-group-options">
				{options.map((option, index) => {
					const isSelected = isOptionSelected(option);
					const isOptionDisabled = isDisabled || option.disabled;

					const optionColors = getOptionColors(option, isSelected);

					return (
						<button
							type="button"
							key={option.id || option.value || index}
							className={`checkbox-group-option ${isSelected ? 'selected' : ''} ${isOptionDisabled ? 'disabled' : ''}`}
							style={{
								backgroundColor: optionColors.background,
								borderColor: optionColors.border,
								color: optionColors.text,
								cursor: isOptionDisabled ? 'not-allowed' : 'pointer'
							}}
							onClick={() => !isOptionDisabled && handleOptionClick(option)}
							disabled={isOptionDisabled}
							aria-pressed={isSelected}>
							<span className="checkbox-group-icon">
								{isSelected ? (
									<svg
										width="12"
										height="12"
										viewBox="0 0 12 12"
										fill="none"
										style={{
											border:
												'1px solid ' +
												(optionColors.checkbox?.border || optionColors.text),
											backgroundColor:
												optionColors.checkbox?.background || optionColors.text
										}}>
										<path
											d="M10 3L4.5 8.5L2 6"
											stroke="#FFFFFF"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								) : (
									<span className="checkbox-group-empty" />
								)}
							</span>
							<span className="checkbox-group-text">
								{option.label || option.value}
							</span>
						</button>
					);
				})}
			</div>
		</div>
	);
};

export default CheckboxGroup;
